import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  Info,
  Maximize2,
  Minimize2,
  Plus,
  Trash2,
} from "lucide-react";
import Button from "../../components/Button";
import { GlassButton } from "../../components/GlassButton";
import { CloseButton } from "../../components/CloseButton";
import { ButtonTooltip } from "../../components/tooltip";
import { ICON_STROKE } from "../../components/icons/icon-tokens";
import { PopupModal } from "../../components/Modal";
import { getUser } from "../../helpers/utils";
import { useConversationHistory, usePricingConversationHistory } from "../hooks/useConversationHistory";

type ChatMode = "chat" | "agent";

/**
 * `.a-modepill` — Chat / Agent segmented control, hand-built to match
 * the Claude-Design mock (translucent white pill with backdrop-blur, ink
 * thumb, Geist 12.5px buttons, 320ms cubic-bezier transition).
 * Replaces the platform-wide `AnimatedToggle` for this surface only —
 * AnimatedToggle uses solid white bg + Tailwind text-xs + spring easing,
 * none of which fit the assistant's premium chrome.
 */
function AssistantModePill({
  mode,
  onChange,
}: {
  mode: ChatMode;
  onChange: (next: ChatMode) => void;
}) {
  const options: { value: ChatMode; label: string }[] = [
    { value: "chat", label: "Chat" },
    { value: "agent", label: "Agent" },
  ];
  return (
    <div className="inline-flex items-center" style={{ gap: 8 }}>
    <motion.div
      role="tablist"
      data-tutorial="chat-mode-toggle"
      layout
      // `layout` makes framer animate this element's TRANSFORM whenever
      // its on-screen position changes (parent flex→grid switch when the
      // ChatV2 panel expands). The whole pill slides from the left of
      // the compact header to the centre of the expanded grid as one
      // smooth motion instead of snapping.
      transition={{ duration: 0 }}
      className="relative inline-flex"
      style={{
        padding: 3,
        // Unified segmented-control radius (matches AnimatedToggle /
        // notification filters / calendar): less-rounded 8px, not a full
        // 999 pill.
        borderRadius: "var(--d-r-lg)",
        background: "rgba(255,255,255,0.6)",
        border: "1px solid var(--d-rule)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        boxShadow: "0 1px 2px rgba(28,26,22,0.04)",
      }}
    >
      {options.map((opt) => {
        const isActive = mode === opt.value;
        return (
          <button
            key={opt.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className="relative cursor-pointer"
            style={{
              minWidth: 68,
              padding: "6px 18px",
              borderRadius: "var(--d-r-md)",
              border: 0,
              background: "transparent",
              fontFamily: "var(--d-font-sans)",
              fontSize: 12.5,
              fontWeight: 500,
              letterSpacing: "-0.005em",
              color: isActive ? "#fff" : "var(--d-ink-muted)",
              transition: "color 240ms cubic-bezier(0.32, 0.72, 0, 1)",
              zIndex: 1,
            }}
          >
            {isActive && (
              <motion.span
                layoutId="chat-mode-active-tab"
                aria-hidden
                className="absolute inset-0"
                style={{
                  borderRadius: "var(--d-r-md)",
                  background: "var(--d-ink)",
                  boxShadow: "0 1px 1px rgba(28,26,22,0.18)",
                  zIndex: -1,
                }}
                transition={{
                  type: "tween",
                  ease: [0.32, 0.72, 0, 1],
                  duration: 0.32,
                }}
              />
            )}
            {opt.label}
          </button>
        );
      })}
    </motion.div>

      {/* Info affordance — single tooltip explaining the Chat vs Agent
          distinction. Only renders here (inside the pill) so it appears
          for pricing customers, the only ones who see the mode toggle. */}
      <button
        type="button"
        aria-label="What's the difference between Chat and Agent?"
        data-tooltip-id="assistant-mode-tooltip"
        data-tooltip-html="<strong>Chat</strong> answers questions and surfaces insights about your hotel. <strong>Agent</strong> takes action — adjusting your rates and pricing strategy for you."
        className="inline-flex items-center justify-center cursor-help flex-shrink-0"
        style={{
          width: 20,
          height: 20,
          padding: 0,
          border: 0,
          background: "transparent",
          color: "var(--d-ink-ghost)",
          transition: "color 160ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--d-ink-muted)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--d-ink-ghost)";
        }}
      >
        <Info className="w-[15px] h-[15px]" strokeWidth={ICON_STROKE} />
      </button>

      {/* Scoped instance placed BELOW the icon — the shared `button-tooltip`
          singleton is top-placed and would clip off-screen when the panel is
          expanded and the pill sits near the top of the viewport. */}
      <ButtonTooltip id="assistant-mode-tooltip" place="bottom" />
    </div>
  );
}

interface ProductionHeaderProps {
  onClose?: () => void;
  onToggleExpanded?: () => void;
  expanded?: boolean;
  currentConversationId?: string;
  onConversationChange?: (conversationId: string, mode: ChatMode) => void;
  onNewChat?: () => void;
  chatMode?: ChatMode;
  onModeChange?: (mode: ChatMode) => void;
}

/**
 * Production Header Component
 *
 * Matches the V1 chat styling with Autumn branding and personalized greeting.
 * Used in ChatV2Production for a polished, user-facing experience.
 */
export function ProductionHeader({
  onClose,
  onToggleExpanded,
  expanded = false,
  currentConversationId,
  onConversationChange,
  onNewChat,
  chatMode = "chat",
  onModeChange,
}: ProductionHeaderProps) {
  const user = getUser();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteSource, setConfirmDeleteSource] = useState<ChatMode | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Track whether the greeting wrapper is mid-animation. `overflow: hidden`
  // is required during height enter/exit so children clip cleanly while
  // the wrapper grows/shrinks — but if it stays hidden at rest the Past
  // Conversations dropdown gets clipped when it opens downward. We set
  // overflow:hidden only while animating, then flip to visible.
  const [greetingAnimating, setGreetingAnimating] = useState(false);

  // Fetch server-side conversation histories
  const { conversations: chatConversations, isLoading: isChatLoading, deleteConversation: deleteChatConversation } =
    useConversationHistory();
  const { conversations: pricingConversations, isLoading: isAgentLoading, deleteConversation: deleteAgentConversation } =
    usePricingConversationHistory();

  const isLoading = isChatLoading || isAgentLoading;

  // Merge and sort all conversations by most recent
  const conversations = useMemo(() => {
    const chat = chatConversations.map((c) => ({
      id: c.id,
      title: c.title,
      lastMessageAt: c.lastMessageAt,
      source: "chat" as ChatMode,
    }));
    const agent = pricingConversations.map((c) => ({
      id: c.chatId,
      title: c.preview,
      lastMessageAt: c.updatedAt,
      source: "agent" as ChatMode,
    }));
    return [...chat, ...agent].sort(
      (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
    );
  }, [chatConversations, pricingConversations]);

  // Close dropdown when clicking outside (skip when confirm modal is open)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (confirmDeleteId) return;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, confirmDeleteId]);

  const handleConversationSelect = (conversationId: string, source: ChatMode) => {
    setIsDropdownOpen(false);
    onConversationChange?.(conversationId, source);
  };

  const handleNewChat = () => {
    setIsDropdownOpen(false);
    onNewChat?.();
  };

  const handleDeleteClick = (conversationId: string, source: ChatMode, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(conversationId);
    setConfirmDeleteSource(source);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId || !confirmDeleteSource) return;

    setDeletingId(confirmDeleteId);
    setConfirmDeleteId(null);

    try {
      if (confirmDeleteSource === "agent") {
        await deleteAgentConversation(confirmDeleteId);
      } else {
        await deleteChatConversation(confirmDeleteId);
      }

      if (confirmDeleteId === currentConversationId) {
        onNewChat?.();
      }
    } catch (error) {
      console.error("[ProductionHeader] Failed to delete conversation:", error);
    } finally {
      setDeletingId(null);
      setConfirmDeleteSource(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };

  // Find current conversation from list, or show "New Chat"
  const currentConversation = currentConversationId
    ? conversations.find((c) => c.id === currentConversationId)
    : null;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return "Today";
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  return (
    <>
      {/* `.a-head` / `.a-main-head` — top bar with mode tabs + window
          controls. Both compact and expanded sit on the cream ambient
          wash with NO bg / NO border-bottom (mock: `.a-main-head` is a
          transparent 3-column grid; `.a-head` is transparent flex
          space-between). The only difference is layout:
            compact:   mode pill LEFT, actions RIGHT (flex space-between)
            expanded:  3-column grid 1fr/auto/1fr, pill in centre column,
                       actions in right column. */}
      <div
        className={`self-stretch flex-shrink-0 bg-transparent ${
          expanded
            ? "grid items-center px-[22px] pt-[18px] pb-[10px]"
            : "flex items-center pt-4 pb-3 px-[18px]"
        }`}
        style={
          expanded ? { gridTemplateColumns: "1fr auto 1fr" } : undefined
        }
      >
        {/* Left column (expanded only) — empty placeholder so the centre
            column visually sits in the middle of the panel. */}
        {expanded && <div aria-hidden />}

        <div
          className={`flex items-center min-w-0 ${
            expanded ? "justify-center" : "flex-shrink-0"
          }`}
        >
          {onModeChange && (
            // No `key` swap and no per-mode `layoutId` — keep ONE pill
            // instance across expand/collapse so the `layout` prop inside
            // can animate its position smoothly instead of remounting.
            <AssistantModePill mode={chatMode} onChange={onModeChange} />
          )}
        </div>

        {/* Spacer pushes the action buttons to the right in compact mode
            (no-op in expanded — the right column of the grid already
            owns the slack). */}
        {!expanded && <div className="flex-1" />}

        {/* Window controls — kit `.a-cbtn` style (32x32 ghost button,
            accent-tint background on hover, ink-muted icon that goes ink
            on hover). In expanded mode they live in the right grid column
            and align to its right edge. */}
        <div
          className={`flex items-center gap-1.5 flex-shrink-0 ${
            expanded ? "justify-end" : ""
          }`}
        >
          {onToggleExpanded && (
            <GlassButton
              onClick={onToggleExpanded}
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? (
                <Minimize2 className="w-[14px] h-[14px]" strokeWidth={ICON_STROKE} />
              ) : (
                <Maximize2 className="w-[14px] h-[14px]" strokeWidth={ICON_STROKE} />
              )}
            </GlassButton>
          )}
          {onClose && (
            <CloseButton onClick={onClose} aria-label="Close chat" />
          )}
        </div>
      </div>

      {/* Greeting Section — hidden in expanded mode (sidebar replaces it).
          Ported from Claude-Design mock: bare `.a-top` block on the cream
          wash (no white bg), 28px Geist greeting with -0.025em tracking,
          and a glassy `.a-thread` card with backdrop-blur for the current
          thread dropdown trigger. */}
      {/* Greeting block — fades out when expanded (the conversation rail
          on the left replaces it). AnimatePresence + opacity/height
          animation keeps the transition smooth instead of snap-removing
          the whole greeting + dropdown + search input. */}
      <AnimatePresence initial={false}>
        {!expanded && (
          <motion.div
            key="greeting-block"
            /* w-full so the greeting + thread select fill the header. On the
               mobile panel the parent is `flex flex-col items-start`, which
               (unlike the desktop column's default `stretch`) would otherwise
               shrink this block to its content width — making the `w-full`
               select only as wide as the "Hello …" heading. */
            className="w-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0 }}
            onAnimationStart={() => setGreetingAnimating(true)}
            onAnimationComplete={() => setGreetingAnimating(false)}
            style={{ overflow: greetingAnimating ? "hidden" : "visible" }}
          >
            <div className="self-stretch px-[22px] pt-1 pb-[18px] bg-transparent flex-shrink-0">
        <h2
          className="m-0 mb-4"
          style={{
            fontFamily: "var(--d-font-sans)",
            fontWeight: 500,
            fontSize: 28,
            lineHeight: 1.15,
            letterSpacing: "-0.025em",
            color: "var(--d-ink)",
          }}
        >
          Hello {user?.firstName || "there"},
        </h2>

        {/* Past Conversations Dropdown */}
        <div className="relative w-full" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-between gap-3 text-left transition-all"
            style={{
              padding: "12px 14px",
              background: "rgba(255,255,255,0.6)",
              border: "1px solid var(--d-rule)",
              borderRadius: 12,
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--d-surface)";
              e.currentTarget.style.borderColor = "var(--d-accent-soft)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.6)";
              e.currentTarget.style.borderColor = "var(--d-rule)";
            }}
          >
            <div className="flex flex-col min-w-0 flex-1">
              <span
                className="truncate"
                style={{
                  fontFamily: "var(--d-font-sans)",
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--d-ink)",
                }}
              >
                {currentConversation?.title || "New Chat"}
              </span>
              <span
                className="truncate"
                style={{
                  fontFamily: "var(--d-font-body)",
                  fontSize: 12,
                  color: "var(--d-ink-ghost)",
                }}
              >
                {currentConversation
                  ? formatTimestamp(currentConversation.lastMessageAt)
                  : "Start a new conversation"}
              </span>
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform flex-shrink-0 ml-2 ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              style={{ color: "var(--d-ink-ghost)" }}
              strokeWidth={ICON_STROKE}
            />
          </button>

          {/* Dropdown Menu — kit chrome (surface bg + rule border + tooltip
              shadow). Was raw Tailwind `bg-white border-border-primary
              shadow-lg`. Items use Geist + mono meta, accent-tint hover. */}
          {isDropdownOpen && (
            <div
              className="absolute z-10 w-full mt-1 max-h-[300px] overflow-y-auto d-scroll-sm"
              style={{
                background: "var(--d-surface)",
                border: "1px solid var(--d-rule)",
                borderRadius: 12,
                boxShadow: "var(--d-shadow-tooltip)",
              }}
            >
              <div>
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="w-full flex items-center px-3 py-2.5 text-left transition-colors"
                  style={{
                    borderBottom: "1px solid var(--d-rule)",
                    background: !currentConversationId
                      ? "var(--d-cream-soft)"
                      : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--d-accent-tint)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = !currentConversationId
                      ? "var(--d-cream-soft)"
                      : "transparent";
                  }}
                >
                  <Plus
                    className="w-4 h-4 mr-2"
                    strokeWidth={ICON_STROKE}
                    style={{ color: "var(--d-accent-deep)" }}
                  />
                  <span
                    style={{
                      fontFamily: "var(--d-font-sans)",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--d-ink)",
                    }}
                  >
                    New Chat
                  </span>
                </button>

                {isLoading && (
                  <div
                    className="px-3 py-4 text-center"
                    style={{
                      fontFamily: "var(--d-font-body)",
                      fontSize: 12,
                      color: "var(--d-ink-muted)",
                    }}
                  >
                    Loading conversations…
                  </div>
                )}

                {!isLoading && conversations.length === 0 && (
                  <div
                    className="px-3 py-4 text-center"
                    style={{
                      fontFamily: "var(--d-font-body)",
                      fontSize: 12,
                      color: "var(--d-ink-muted)",
                    }}
                  >
                    No past conversations
                  </div>
                )}

                {!isLoading &&
                  conversations.map((conversation) => {
                    const isActive = conversation.id === currentConversationId;
                    return (
                      <div
                        key={conversation.id}
                        className="group relative flex items-center transition-colors"
                        style={{
                          background: isActive
                            ? "var(--d-cream-soft)"
                            : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive)
                            e.currentTarget.style.background =
                              "var(--d-accent-tint)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleConversationSelect(
                              conversation.id,
                              conversation.source,
                            )
                          }
                          className="flex-1 flex flex-col px-3 py-2 text-left min-w-0"
                          disabled={deletingId === conversation.id}
                        >
                          <span
                            className="truncate"
                            style={{
                              fontFamily: "var(--d-font-sans)",
                              fontSize: 13,
                              fontWeight: 500,
                              color: "var(--d-ink)",
                            }}
                          >
                            {conversation.title}
                          </span>
                          <span
                            className="truncate"
                            style={{
                              fontFamily: "var(--d-font-mono)",
                              fontSize: 10.5,
                              color: "var(--d-ink-ghost)",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {formatTimestamp(conversation.lastMessageAt)}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) =>
                            handleDeleteClick(
                              conversation.id,
                              conversation.source,
                              e,
                            )
                          }
                          disabled={deletingId === conversation.id}
                          className="opacity-0 group-hover:opacity-100 mr-1 inline-flex items-center justify-center transition-opacity flex-shrink-0"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: 0,
                            background: "transparent",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "var(--d-neg-soft)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                          aria-label="Delete conversation"
                          title="Delete conversation"
                        >
                          {deletingId === conversation.id ? (
                            <div
                              className="w-3 h-3 rounded-full animate-spin"
                              style={{
                                border: "2px solid var(--d-ink-muted)",
                                borderTopColor: "transparent",
                              }}
                            />
                          ) : (
                            <Trash2
                              className="w-[14px] h-[14px]"
                              strokeWidth={ICON_STROKE}
                              style={{ color: "var(--d-neg-text)" }}
                            />
                          )}
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Confirmation Dialog */}
      {confirmDeleteId && (
        <PopupModal
          onClose={handleCancelDelete}
          contentClassName="w-[500px] mx-auto"
          shouldStopPropagation={false}
        >
          <div className="space-y-4">
            <h3
              className="font-semibold"
              style={{
                fontFamily: "var(--d-font-sans)",
                fontSize: 18,
                color: "var(--d-ink)",
                letterSpacing: "-0.01em",
              }}
            >
              Delete Conversation
            </h3>
            <p
              style={{
                fontFamily: "var(--d-font-body)",
                fontSize: 13,
                color: "var(--d-ink-muted)",
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to delete this conversation?
            </p>
          </div>
          <div className="flex flex-row gap-3 ml-auto">
            <Button
              variant="outline"
              className="px-6"
              onClick={handleCancelDelete}
              disabled={deletingId !== null}
            >
              Cancel
            </Button>
            {/* Destructive action — native button styled with kit neg
                tokens. `<Button>` doesn't expose a `style` prop, so we
                drop down to a raw button for this one CTA. */}
            <button
              type="button"
              className="d-btn d-btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "var(--d-neg-text)",
                borderColor: "var(--d-neg-text)",
                color: "#fff",
              }}
              onClick={handleConfirmDelete}
              disabled={deletingId !== null}
            >
              {deletingId !== null ? "Deleting…" : "Delete"}
            </button>
          </div>
        </PopupModal>
      )}
    </>
  );
}
